import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Copy, Trash2, Plus, Play, Save } from 'lucide-react';
import { GlassCard } from '@/components/GlassCard';
import { PrimaryButton } from '@/components/PrimaryButton';
import { DurationStepper } from '@/components/DurationStepper';
import { Input } from '@/components/ui/input';
import { useApp } from '@/context/AppContext';
import {
  createBlock,
  createRoutine,
  calcTotalDuration,
  formatTime,
  uid,
} from '@/models/models';
import { toast } from 'sonner';

const COLORS = ['#EF4444', '#22C55E', '#38BDF8', '#F59E0B', '#A78BFA'];

const BlockRow = ({ block, onChange, onDuplicate, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: block.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 'auto',
    opacity: isDragging ? 0.9 : 1,
  };
  const upd = (patch) => onChange({ ...block, ...patch });

  return (
    <div ref={setNodeRef} style={style} data-testid={`builder-block-${block.id}`}>
      <GlassCard className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <button
            {...attributes}
            {...listeners}
            aria-label="Drag to reorder"
            className="cursor-grab touch-none text-[#CBD5E1] active:cursor-grabbing"
            data-testid={`drag-${block.id}`}
          >
            <GripVertical size={20} />
          </button>
          <Input
            value={block.name}
            onChange={(e) => upd({ name: e.target.value })}
            aria-label="Exercise name"
            data-testid={`name-${block.id}`}
            className="border-white/10 bg-white/5 text-base font-bold text-white"
          />
          <button
            onClick={onDuplicate}
            aria-label="Duplicate block"
            data-testid={`duplicate-${block.id}`}
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/5 text-[#38BDF8] hover:bg-white/10"
          >
            <Copy size={17} />
          </button>
          <button
            onClick={onDelete}
            aria-label="Delete block"
            data-testid={`delete-${block.id}`}
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/5 text-[#F87171] hover:bg-[#EF4444]/10"
          >
            <Trash2 size={17} />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <DurationStepper
            label="Work"
            value={block.work}
            step={5}
            min={5}
            suffix="s"
            accent="#EF4444"
            onChange={(v) => upd({ work: v })}
            testId={`work-${block.id}`}
          />
          <DurationStepper
            label="Rest"
            value={block.rest}
            step={5}
            min={0}
            suffix="s"
            accent="#22C55E"
            onChange={(v) => upd({ rest: v })}
            testId={`rest-${block.id}`}
          />
          <DurationStepper
            label="Rounds"
            value={block.rounds}
            step={1}
            min={1}
            max={99}
            suffix="x"
            accent="#38BDF8"
            onChange={(v) => upd({ rounds: v })}
            testId={`rounds-${block.id}`}
          />
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#CBD5E1]">
            Color
          </span>
          {COLORS.map((c) => (
            <button
              key={c}
              onClick={() => upd({ color: c })}
              aria-label={`Color ${c}`}
              className="h-7 w-7 rounded-full border-2 transition-transform hover:scale-110"
              style={{
                background: c,
                borderColor: block.color === c ? '#FFFFFF' : 'transparent',
              }}
            />
          ))}
        </div>
      </GlassCard>
    </div>
  );
};

export const Builder = () => {
  const navigate = useNavigate();
  const { saveRoutine } = useApp();
  const [name, setName] = useState('My Routine');
  const [blocks, setBlocks] = useState([
    createBlock({ name: 'Burpees', color: '#EF4444' }),
    createBlock({ name: 'Push-ups', work: 30, rest: 15, rounds: 4, color: '#38BDF8' }),
  ]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const total = useMemo(
    () => calcTotalDuration({ blocks }),
    [blocks]
  );

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setBlocks((items) => {
      const oldIndex = items.findIndex((b) => b.id === active.id);
      const newIndex = items.findIndex((b) => b.id === over.id);
      return arrayMove(items, oldIndex, newIndex);
    });
  };

  const updateBlock = (updated) =>
    setBlocks((bs) => bs.map((b) => (b.id === updated.id ? updated : b)));
  const addBlock = () =>
    setBlocks((bs) => [...bs, createBlock({ name: `Exercise ${bs.length + 1}` })]);
  const duplicateBlock = (block) =>
    setBlocks((bs) => {
      const idx = bs.findIndex((b) => b.id === block.id);
      const copy = { ...block, id: uid('block') };
      const next = [...bs];
      next.splice(idx + 1, 0, copy);
      return next;
    });
  const deleteBlock = (id) =>
    setBlocks((bs) => (bs.length > 1 ? bs.filter((b) => b.id !== id) : bs));

  const persist = () => {
    const routine = createRoutine({ name, blocks });
    saveRoutine(routine);
    toast.success('Routine saved');
    return routine;
  };

  const startNow = () => {
    const routine = persist();
    navigate(`/workout/${routine.id}`);
  };

  return (
    <div className="flex flex-col gap-6">
      <header className="flex items-end justify-between">
        <div>
          <p className="text-sm font-medium text-[#CBD5E1]">Workout Builder</p>
          <h1 className="mt-1 text-3xl font-extrabold text-white">Design it</h1>
        </div>
        <div className="text-right">
          <p className="text-xs uppercase tracking-wider text-[#CBD5E1]">Total</p>
          <p className="text-2xl font-extrabold text-[#38BDF8] tabular-nums">
            {formatTime(total)}
          </p>
        </div>
      </header>

      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        aria-label="Routine name"
        data-testid="routine-name-input"
        className="border-white/10 bg-white/5 text-lg font-bold text-white"
      />

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={onDragEnd}
      >
        <SortableContext
          items={blocks.map((b) => b.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="flex flex-col gap-4">
            {blocks.map((block) => (
              <BlockRow
                key={block.id}
                block={block}
                onChange={updateBlock}
                onDuplicate={() => duplicateBlock(block)}
                onDelete={() => deleteBlock(block.id)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <motion.button
        whileTap={{ scale: 0.98 }}
        onClick={addBlock}
        data-testid="add-block-btn"
        className="flex min-h-[56px] items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-white/15 font-bold text-[#CBD5E1] hover:border-[#38BDF8]/50 hover:text-white"
      >
        <Plus size={20} /> Add Exercise
      </motion.button>

      <div className="grid grid-cols-2 gap-3">
        <PrimaryButton variant="ghost" onClick={persist} data-testid="save-routine-btn">
          <Save size={18} /> Save
        </PrimaryButton>
        <PrimaryButton variant="work" onClick={startNow} data-testid="start-routine-btn">
          <Play size={18} fill="currentColor" /> Start
        </PrimaryButton>
      </div>
    </div>
  );
};

export default Builder;
